import React, { useState, useEffect } from 'react';
import { Modal } from '../../layout';
import { Button } from '../../layout/buttons';
import { Icon } from '../../layout/icons/Icons';
import styles from '../Canvasses.module.css';
import reportStyles from './ReportSection.module.css';
import { useUserRoles } from '../../../utils/useUserRoles';

const InterviewModal = ({ isOpen, onClose, sessionId, canvassId }) => {
  const { isAdmin } = useUserRoles();
  const [interviewData, setInterviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !sessionId) return;

    const fetchInterview = async () => {
      setLoading(true);
      setError('');
      try {
        const { fetchInterviewBySessionId } = await import('../../../utils/interviews');
        const result = await fetchInterviewBySessionId({ sessionId });
        setInterviewData(result);
      } catch (e) {
        setError(e?.message || 'Failed to load interview data');
      } finally {
        setLoading(false);
      }
    };

    fetchInterview();
  }, [isOpen, sessionId]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleString();
  };

  const formatEmotionStrength = (strength) => {
    if (strength === null || strength === undefined) return '—';
    const numStrength = typeof strength === 'string' ? parseFloat(strength) : strength;
    if (isNaN(numStrength)) return '—';
    return numStrength.toFixed(2);
  };

  const getEmotionStrengthStyle = (strength) => {
    if (strength === null || strength === undefined) return {};
    const numStrength = typeof strength === 'string' ? parseFloat(strength) : strength;
    if (isNaN(numStrength)) return {};
    
    // Scale font size from 0.75em (0.0) to 1.25em (1.0)
    const fontSize = 0.75 + (numStrength * 0.5);
    
    // Scale opacity from 0.4 (0.0) to 1.0 (1.0)
    const opacity = 0.4 + (numStrength * 0.6);
    
    // Color gradient from green (low) through yellow (medium) to red (high)
    let color;
    if (numStrength >= 0.8) {
      color = 'var(--brand-as-red-700, #b91c1c)';
    } else if (numStrength >= 0.5) {
      color = 'var(--brand-as-yellow-700, #a16207)';
    } else {
      color = 'var(--brand-as-green-700, #15803d)';
    }
    
    // Background color with varying intensity
    let backgroundColor;
    if (numStrength >= 0.8) {
      backgroundColor = 'var(--brand-as-red-50, #fef2f2)';
    } else if (numStrength >= 0.5) {
      backgroundColor = 'var(--brand-as-yellow-50, #fffbeb)';
    } else {
      backgroundColor = 'var(--brand-as-green-50, #f0fdf4)';
    }
    
    return {
      fontSize: `${fontSize}em`,
      opacity,
      color,
      backgroundColor,
      fontWeight: numStrength >= 0.7 ? '600' : numStrength >= 0.4 ? '500' : '400',
      padding: '2px 6px',
      borderRadius: '4px',
      border: `1px solid ${color}20`,
      display: 'inline-block',
      minWidth: `${2 + numStrength * 2}em`,
      textAlign: 'center'
    };
  };

  const navigateToInterviewsPage = () => {
    if (canvassId) {
      const interviewId = interviewData?.metadata?.id || sessionId;
      window.location.href = `/canvasses/${canvassId}/interviews${interviewId ? `?sessionId=${interviewId}` : ''}`;
    }
  };

  
  
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Interview Details"
      actions={
        isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="me-2"
            onClick={navigateToInterviewsPage}
            disabled={!canvassId}
          >
            View Full Interview
            <Icon name="arrows/arrow_right" color="var(--btn-accent, #374151)"  />
          </Button>
        )
      }
    >
      <div className={styles.canvassModalContent}>
        <div className={`mb-3 ${styles.canvassModalHeader}`}>
          <p className="mb-0">
            Interview Details — {sessionId}
          </p>
        </div>

        <div className={`${styles.canvassModalBody} side-panel-body`}>
          {loading && (
            <p className="text-muted">Loading interview data…</p>
          )}

          {error && (
            <p className="text-danger small">{error}</p>
          )}

          {!loading && !error && interviewData && (
            <>
              <div className={`${styles.canvassModalSection} user-info-section`}>
                <h6>Interview Information</h6>
                <div className={`${styles.canvassInfoGrid} info-grid`}>
                  <div className={`${styles.canvassInfoItem} info-item`}>
                    <span className="info-label">Interview ID:</span>
                    <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.interview?.id || '—'}</span>
                  </div>
                  <div className={`${styles.canvassInfoItem} info-item`}>
                    <span className="info-label">Session ID:</span>
                    <span className={`${styles.canvassInfoValue} info-value`}>{sessionId || '—'}</span>
                  </div>
                  <div className={`${styles.canvassInfoItem} info-item`}>
                    <span className="info-label">Status:</span>
                    <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.interview?.state || '—'}</span>
                  </div>
                  <div className={`${styles.canvassInfoItem} info-item`}>
                    <span className="info-label">Created:</span>
                    <span className={`${styles.canvassInfoValue} info-value`}>{formatTimestamp(interviewData.interview?.metadata?.createdAt)}</span>
                  </div>
                  <div className={`${styles.canvassInfoItem} info-item`}>
                    <span className="info-label">Completed:</span>
                    <span className={`${styles.canvassInfoValue} info-value`}>{formatTimestamp(interviewData.interview?.metadata?.completedAt)}</span>
                  </div>
                  <div className={`${styles.canvassInfoItem} info-item`}>
                    <span className="info-label">Questions Completed:</span>
                    <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.interview?.metadata?.questionsCompleted || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Interview Analysis */}
              {interviewData.interview?.interviewAnalysis && (
                <div className={`${styles.canvassModalSection} user-info-section`}>
                  <h6>Interview Analysis</h6>
                  <div className={`${styles.canvassInfoGrid} info-grid`}>
                    <div className={`${styles.canvassInfoItem} info-item`}>
                      <span className="info-label">Participant ID:</span>
                      <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.interview.interviewAnalysis.participant_id || '—'}</span>
                    </div>
                    <div className={`${styles.canvassInfoItem} info-item`}>
                      <span className="info-label">Psychographic Archetype:</span>
                      <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.interview.interviewAnalysis.psychographic_archetype || '—'}</span>
                    </div>
                    <div className={`${styles.canvassInfoItem} info-item`}>
                      <span className="info-label">Dominant Emotion:</span>
                      <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.interview.interviewAnalysis.dominant_emotion || '—'}</span>
                    </div>
                  </div>
                  
                  {interviewData.interview.interviewAnalysis.narrative_summary && (
                    <div className="mt-3">
                      <h6>Narrative Summary</h6>
                      <p className="text-muted" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {interviewData.interview.interviewAnalysis.narrative_summary}
                      </p>
                    </div>
                  )}

                  {interviewData.interview.interviewAnalysis.emotions && (
                    <div className="mt-3">
                      <h6>Emotions</h6>
                      <div className={`${styles.canvassInfoGrid} info-grid`}>
                        {interviewData.interview.interviewAnalysis.emotions.map((emotion, index) => (
                          <div key={index} className={`${styles.canvassInfoItem} info-item`}>
                            <span className="info-label">{emotion.emotion || '—'}:</span>
                            <span 
                              className={`${styles.canvassInfoValue} info-value emotion-strength-value`}
                              style={getEmotionStrengthStyle(emotion.strength)}
                            >
                              {formatEmotionStrength(emotion.strength)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Interview Insights */}
              {interviewData.insights && Object.keys(interviewData.insights).length > 0 && (
                <div className={`${styles.canvassModalSection} user-info-section`}>
                  <h6>Interview Insights</h6>
                  
                  {interviewData.insights.participant_profile && (
                    <div className="mb-4">
                      <h6 className="text-muted">Participant Profile</h6>
                      <div className={`${styles.canvassInfoGrid} info-grid`}>
                        <div className={`${styles.canvassInfoItem} info-item`}>
                          <span className="info-label">Role:</span>
                          <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.insights.participant_profile.role || '—'}</span>
                        </div>
                        <div className={`${styles.canvassInfoItem} info-item`}>
                          <span className="info-label">Context:</span>
                          <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.insights.participant_profile.context || '—'}</span>
                        </div>
                      </div>
                      
                      {interviewData.insights.participant_profile.primary_needs && Array.isArray(interviewData.insights.participant_profile.primary_needs) && interviewData.insights.participant_profile.primary_needs.length > 0 && (
                        <div className="mt-3">
                          <h6 className="text-muted">Primary Needs</h6>
                          <div style={{ width: '100%' }}>
                            <div style={{ padding: '12px', borderRadius: '6px', border: '1px solid var(--brand-as-neutral-200, #e5e7eb)' }}>
                              <ul style={{ margin: '0', paddingLeft: '20px', textAlign: 'left' }}>
                                {interviewData.insights.participant_profile.primary_needs.map((need, index) => (
                                  <li key={index} style={{ marginBottom: '4px', color: 'var(--brand-as-neutral-900, #111827)' }}>{need || '—'}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}

                      {interviewData.insights.participant_profile.persona_inferred && (
                        <div className="mt-3">
                          <h6 className="text-muted">Inferred Persona</h6>
                          <div className={`${styles.canvassInfoGrid} info-grid`}>
                            <div className={`${styles.canvassInfoItem} info-item`}>
                              <span className="info-label">Age Cohort:</span>
                              <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.insights.participant_profile.persona_inferred.age_cohort || '—'}</span>
                            </div>
                            <div className={`${styles.canvassInfoItem} info-item`}>
                              <span className="info-label">Style Orientation:</span>
                              <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.insights.participant_profile.persona_inferred.style_orientation || '—'}</span>
                            </div>
                            <div className={`${styles.canvassInfoItem} info-item`}>
                              <span className="info-label">Decision Style:</span>
                              <span className={`${styles.canvassInfoValue} info-value`}>{interviewData.insights.participant_profile.persona_inferred.decision_style || '—'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {interviewData.insights.core_themes && Array.isArray(interviewData.insights.core_themes) && interviewData.insights.core_themes.length > 0 && (
                    <div>
                      <h6 className="text-muted">Core Themes</h6>
                      {interviewData.insights.core_themes.map((theme, index) => (
                        <div key={index} className="mb-3 p-3 border rounded">
                          <h5 className="mb-2">{theme.label}</h5>
                          <div className="mb-2" style={{ textAlign: 'left' }}>
                            <strong>Emotional Impact:</strong> <span className="text-muted">{theme.emotional_impact}</span>
                          </div>
                          {theme.business_risk && (
                            <div className="mb-2" style={{ textAlign: 'left' }}>
                              <strong>Business Risk:</strong> <span className="text-muted">{theme.business_risk}</span>
                            </div>
                          )}
                          {theme.business_opportunity && (
                            <div style={{ textAlign: 'left' }}>
                              <strong>Business Opportunity:</strong> <span className="text-muted">{theme.business_opportunity}</span>
                            </div>
                          )}
                          <div className="mb-2" style={{ textAlign: 'left' }}>
                            <strong>Evidence:</strong>
                            <ul className="mb-2">
                              {theme.evidence?.map((evidence, i) => (
                                <li key={i} style={{ fontSize: '0.875em', textAlign: 'left' }}>{evidence}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Actionable Recommendations */}
              {interviewData.insights && interviewData.insights.actionable_recommendations && Array.isArray(interviewData.insights.actionable_recommendations) && interviewData.insights.actionable_recommendations.length > 0 && (
                <div className={`${styles.canvassModalSection} user-info-section`}>
                  <h6>Actionable Recommendations</h6>
                  {interviewData.insights.actionable_recommendations.map((recommendation, index) => (
                    <div key={index} className="mb-4 p-3 border rounded" style={{ textAlign: 'left' }}>
                      <div className="mb-2">
                        <span className={`badge me-2 ${
                          recommendation.priority === 'critical' ? 'bg-danger' :
                          recommendation.priority === 'very_high' ? 'bg-danger' :
                          recommendation.priority === 'high' ? 'bg-warning' :
                          recommendation.priority === 'medium' ? 'bg-info' :
                          recommendation.priority === 'low' ? 'bg-success' : 'bg-secondary'
                        }`}>
                          {recommendation.priority || 'medium'}
                        </span>
                        <h6 className="d-inline-block mb-0">{recommendation.title}</h6>
                      </div>
                      <div className="mb-3">
                        <p className="mb-0 text-muted">{recommendation.description}</p>
                      </div>
                      {recommendation.specific_suggestions && (
                        <div className="mb-3">
                          <strong>Specific Suggestions:</strong>
                          <ul className="mb-0 mt-2">
                            {recommendation.specific_suggestions.map((suggestion, i) => (
                              <li key={i} style={{ fontSize: '0.875em', textAlign: 'left' }}>{suggestion}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {recommendation.expected_emotional_outcome && (
                        <div className="mb-2">
                          <strong>Expected Emotional Outcome:</strong> <span className="text-muted">{recommendation.expected_emotional_outcome}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default InterviewModal;
